import { normalizeDriverApplication } from './driver-applications';

function isMissingRpcError(message) {
  return /admin_set_driver_application_status|schema cache|could not find/i.test(
    String(message || '')
  );
}

/**
 * Approve / reject / update driver application status (ops admin).
 * Uses security-definer RPC when deployed; falls back to direct table update.
 */
export async function setDriverApplicationStatus(
  supabaseClient,
  driverId,
  { status, adminNotes, referralCode }
) {
  if (!supabaseClient || !driverId) {
    return { data: null, error: 'Missing driver.' };
  }

  const { data: rpcData, error: rpcError } = await supabaseClient.rpc(
    'admin_set_driver_application_status',
    {
      p_driver_id: driverId,
      p_status: status,
      p_admin_notes: adminNotes?.trim() || null,
      p_referral_code: referralCode || null,
    }
  );

  if (!rpcError && rpcData) {
    return { data: normalizeDriverApplication(rpcData), error: '' };
  }

  const rpcMessage = rpcError?.message || '';
  if (rpcError && !isMissingRpcError(rpcMessage)) {
    const hint =
      /permission|row-level|42501/i.test(rpcMessage)
        ? ' Run supabase/fix-admin-driver-approval.sql in Supabase and confirm your profile has is_admin = true.'
        : '';
    return { data: null, error: rpcMessage + hint };
  }

  const payload = {
    status,
    admin_notes: adminNotes?.trim() || null,
    reviewed_at: new Date().toISOString(),
  };
  if (referralCode) {
    payload.referral_code = referralCode;
  }

  const { data, error } = await supabaseClient
    .from('drivers')
    .update(payload)
    .eq('id', driverId)
    .select('*')
    .single();

  if (error) {
    const msg = error.message || 'Could not update driver status.';
    const hint =
      /permission|row-level|42501/i.test(msg) || /protect|trigger/i.test(msg)
        ? ' Run supabase/fix-admin-driver-approval.sql in Supabase SQL editor.'
        : '';
    return { data: null, error: msg + hint };
  }

  if (data && String(data.status || '').toLowerCase() !== String(status).toLowerCase()) {
    return {
      data: null,
      error:
        'Status did not change. Run supabase/fix-admin-driver-approval.sql in Supabase (admin trigger was blocking approval).',
    };
  }

  return { data: normalizeDriverApplication(data), error: '' };
}
